import { faEdit, faHandBackFist, faHandSparkles, faChevronDown, faChevronUp, faXmark, faEye } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useRef, useEffect } from 'react'
import useAuth from '../hooks/useAuth'

const PolicyBanner = () => {
    const { createInfo, auth, setAuth, userPolicy } = useAuth()
    const [position, setPosition] = useState({ x: window.innerWidth - 625, y: window.innerHeight - 400 })
    const [isDragging, setIsDragging] = useState(false)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [isEditing, setIsEditing] = useState({
        product: false,
        status: false,
        department: false,
        inception: false,
        expire: false,
        staffInception: false,
        staffEndDate: false,
        staffDob: false,
        staffSlimmingWorld: false,
        staffStatus: false,
        staffArchived: false
    })
    const [isMinimized, setIsMinimized] = useState(false)
    const [isMinimizeButtonHover, setIsMinimizeButtonHover] = useState(false)
    const [isHidden, setIsHidden] = useState(false)
    const [availableProducts, setAvailableProducts] = useState([])
    const [activeTab, setActiveTab] = useState('policy')
    const [showAddressSection, setShowAddressSection] = useState(false)
    const bannerRef = useRef(null)
    const initialValuesRef = useRef(null)

    useEffect(() => {
        if (userPolicy && !initialValuesRef.current) {
            initialValuesRef.current = {
                product: userPolicy.product,
                status: userPolicy.status,
                department: userPolicy.department
            }
        }
    }, [userPolicy])

    useEffect(() => {
        const hiddenState = localStorage.getItem('policyBannerHidden')
        if (hiddenState === 'true') {
            setIsHidden(true)
        }
    }, [])

    useEffect(() => {
        if (userPolicy?.department) {
            const products = getAvailableProducts()
            if (products.length === 0) {
                createInfo('error', 'No Products Found')
                setAvailableProducts(['No Products Available'])
            } else {
                setAvailableProducts(products)
                if (!products.includes(userPolicy.product) && products.length > 0) {
                    updatePolicyField('product', products[0])
                }
            }
        }
    }, [userPolicy?.department])

    const getAvailableProducts = () => {
        if (userPolicy?.department === 'SAS') {
            return ['SAS Policy']
        }
        if (userPolicy?.department === 'Mutual') {
            return ['Enhanced v2']
        }
        if (userPolicy?.department === 'AIS') {
            return ['AIS Policy']
        }
        if (userPolicy?.department === 'GBS') {
            return ['GBS Product']
        }
        if (userPolicy?.department === 'SWB') {
            return [
                'SWB Premium',
                'SWB Essential',
                'SWB OH+',
                'SWB OH+ Only',
            ]
        }
        return []
    }

    const dropdownOptions = {
        status: [
            'live',
            'accepted',
            'expiring',
            'previous',
            'pending',
        ],
        department: [
            'SAS',
            'Mutual',
            'SWB',
            'AIS',
            'GBS'
        ]
    }



    const updatePolicyField = (field, value) => {
        if (field === 'inception' || field === 'expire') {
            const inceptionDate = field === 'inception' ? new Date(value) : new Date(userPolicy.inception)
            const expireDate = field === 'expire' ? new Date(value) : new Date(userPolicy.expire)

            if (inceptionDate > expireDate) {
                alert('Inception date cannot be after expire date')
                return
            }
        }

        setAuth(prevAuth => ({
            ...prevAuth,
            user: {
                ...prevAuth.user,
                client: {
                    ...prevAuth.user.client,
                    policy: {
                        ...prevAuth.user.client.policy,
                        [field]: value
                    }
                }
            }
        }))
        setIsEditing(prev => ({ ...prev, [field]: false }))
    }

    const updatePolicyStaffField = (field, value) => {
        if (field === 'inception_date' || field === 'end_date') {
            const staffData = auth.policyStaff
            const inceptionDate = field === 'inception_date' ? new Date(value) : new Date(staffData.inception_date)
            const endDate = field === 'end_date' ? new Date(value) : new Date(staffData.end_date)

            if (inceptionDate > endDate) {
                alert('Inception date cannot be after end date')
                return
            }
        }

        setAuth(prevAuth => ({
            ...prevAuth,
            policyStaff: {
                ...prevAuth.policyStaff,
                [field]: value
            }
        }))

        const editingStateMap = {
            'inception_date': 'staffInception',
            'end_date': 'staffEndDate',
            'date_of_birth': 'staffDob',
            'accessed_slimming_world': 'staffSlimmingWorld',
            'policy_staff_status': 'staffStatus',
            'archived': 'staffArchived'
        }
        const editingState = editingStateMap[field]
        if (editingState) {
            setIsEditing(prev => ({ ...prev, [editingState]: false }))
        }
    }

    const handleMouseDown = (e) => {
        setIsDragging(true)
        const rect = bannerRef.current.getBoundingClientRect()
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        })
        e.preventDefault()
    }

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - dragOffset.x
                const newY = e.clientY - dragOffset.y

                const maxX = window.innerWidth - (bannerRef.current?.offsetWidth || 480)
                const maxY = window.innerHeight - (bannerRef.current?.offsetHeight || 400)

                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY))
                })
            }
        }

        const handleMouseUp = () => {
            setIsDragging(false)
        }

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isDragging, dragOffset])

    useEffect(() => {
        const handleResize = () => {
            const maxX = window.innerWidth - (bannerRef.current?.offsetWidth || 480)
            const maxY = window.innerHeight - (bannerRef.current?.offsetHeight || 400)

            setPosition(prev => ({
                x: Math.max(0, Math.min(prev.x, maxX)),
                y: Math.max(0, Math.min(prev.y, maxY))
            }))
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const handleHideBanner = () => {
        setIsHidden(true)
        localStorage.setItem('policyBannerHidden', 'true')
    }

    const handleShowBanner = () => {
        setIsHidden(false)
        localStorage.setItem('policyBannerHidden', 'false')
    }

    if (!userPolicy || Object.keys(userPolicy).length === 0) {
        return null
    }

    return (
        <>
            {!isHidden && (
                <div ref={bannerRef}
                    className={`fixed z-[999] bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg shadow-xl border border-purple-300/20 max-w-xl w-full backdrop-blur-sm transition-shadow duration-200 ${isDragging && !isMinimizeButtonHover ? 'shadow-2xl scale-105' : 'shadow-xl'}`}
                    style={{
                        left: `${position.x}px`,
                        top: `${position.y}px`,
                        cursor: isDragging ? 'grabbing' : 'default'
                    }}
                >
                    <div className={`bg-purple-700/30 px-4 py-3 ${isMinimized ? 'rounded-lg' : 'rounded-t-lg border-b border-purple-400/20'} cursor-grab active:cursor-grabbing select-none`} onMouseDown={handleMouseDown} >
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-purple-100">Policy Information</h3>
                            <div className="flex items-center space-x-2">
                                <button
                                    className="text-purple-200 hover:text-white transition-colors p-2 rounded hover:bg-purple-600/30 z-10"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        setIsMinimized(!isMinimized)
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                    }}
                                    onMouseEnter={() => setIsMinimizeButtonHover(true)}
                                    onMouseLeave={() => setIsMinimizeButtonHover(false)}
                                    title={isMinimized ? "Expand" : "Minimize"}
                                >
                                    {isMinimized ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                    )}
                                </button>
                                <button
                                    className="text-purple-200 hover:text-red-300 transition-colors p-2 rounded hover:bg-red-600/30 z-10"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        handleHideBanner()
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault()
                                    }}
                                    title="Hide Banner Permanently"
                                >
                                    <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                                </button>
                                <div className="flex space-x-1 ml-4">
                                    {isDragging ? (
                                        <FontAwesomeIcon icon={faHandBackFist} />
                                    ) : (
                                        <FontAwesomeIcon icon={faHandSparkles} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {!isMinimized && (
                        <div className="p-4 space-y-3">
                            <div className="flex bg-purple-500/20 rounded-lg p-1">
                                <button
                                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'policy'
                                        ? 'bg-purple-600 text-white'
                                        : 'text-purple-200 hover:text-white hover:bg-purple-600/50'
                                        }`}
                                    onClick={() => setActiveTab('policy')}
                                >
                                    Policy
                                </button>
                                <button
                                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'staff'
                                        ? 'bg-purple-600 text-white'
                                        : 'text-purple-200 hover:text-white hover:bg-purple-600/50'
                                        }`}
                                    onClick={() => setActiveTab('staff')}
                                >
                                    Policy Staff
                                </button>
                            </div>

                            {activeTab === 'policy' && (
                                <>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="bg-purple-500/20 p-2 rounded text-center min-h-[60px] flex flex-col">
                                            <div className="text-xs text-purple-200 uppercase tracking-wide mb-1">Department</div>
                                            <div className="flex-1 flex items-center justify-center">
                                                {isEditing.department ? (
                                                    <select
                                                        className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5 w-full"
                                                        value={userPolicy.department}
                                                        onChange={(e) => updatePolicyField('department', e.target.value)}
                                                        onBlur={() => setIsEditing(prev => ({ ...prev, department: false }))}
                                                        autoFocus
                                                    >
                                                        {dropdownOptions.department.map(option => (
                                                            <option key={option} value={option} className="bg-purple-700">{option}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div
                                                        className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center justify-center gap-1"
                                                        onClick={() => setIsEditing(prev => ({ ...prev, department: true }))}
                                                    >
                                                        {userPolicy.department}
                                                        <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-purple-500/20 p-2 rounded text-center min-h-[60px] flex flex-col">
                                            <div className="text-xs text-purple-200 uppercase tracking-wide mb-1">Product</div>
                                            <div className="flex-1 flex items-center justify-center">
                                                {isEditing.product ? (
                                                    <select
                                                        className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5 w-full"
                                                        value={userPolicy.product}
                                                        onChange={(e) => updatePolicyField('product', e.target.value)}
                                                        onBlur={() => setIsEditing(prev => ({ ...prev, product: false }))}
                                                        autoFocus
                                                    >
                                                        {availableProducts.map(option => (
                                                            <option key={option} value={option} className="bg-purple-700">{option}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div
                                                        className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center justify-center gap-1"
                                                        onClick={() => setIsEditing(prev => ({ ...prev, product: true }))}
                                                    >
                                                        {userPolicy.product}
                                                        <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-purple-500/20 p-2 rounded text-center min-h-[60px] flex flex-col">
                                            <div className="text-xs text-purple-200 uppercase tracking-wide mb-1">Status</div>
                                            <div className="flex-1 flex items-center justify-center">
                                                {isEditing.status ? (
                                                    <select
                                                        className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5 w-full"
                                                        value={userPolicy.status}
                                                        onChange={(e) => updatePolicyField('status', e.target.value)}
                                                        onBlur={() => setIsEditing(prev => ({ ...prev, status: false }))}
                                                        autoFocus
                                                    >
                                                        {dropdownOptions.status.map(option => (
                                                            <option key={option} value={option} className="bg-purple-700 capitalize">{option}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div
                                                        className="text-sm font-medium cursor-pointer hover:text-purple-100 capitalize flex items-center justify-center gap-1"
                                                        onClick={() => setIsEditing(prev => ({ ...prev, status: true }))}
                                                    >
                                                        {userPolicy.status}
                                                        <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Policy ID:</span>
                                            <span className="text-sm font-medium">{userPolicy.id}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Reference:</span>
                                            <span className="text-sm font-medium">{userPolicy.reference}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Insurer:</span>
                                            <span className="text-sm font-medium">{userPolicy.insurer}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Inception:</span>
                                            {isEditing.inception ? (
                                                <input
                                                    type="date"
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={userPolicy.inception}
                                                    onChange={(e) => updatePolicyField('inception', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, inception: false }))}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, inception: true }))}
                                                >
                                                    {userPolicy.inception}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Expires:</span>
                                            {isEditing.expire ? (
                                                <input
                                                    type="date"
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={userPolicy.expire}
                                                    onChange={(e) => updatePolicyField('expire', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, expire: false }))}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, expire: true }))}
                                                >
                                                    {userPolicy.expire}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-purple-200">Premium:</span>
                                            <span className="text-sm font-medium">{userPolicy.premium}</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {activeTab === 'staff' && auth.policyStaff && (
                                <div className="space-y-3">
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Name:</span>
                                            <span className="text-sm font-medium">{auth.policyStaff.name}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Email:</span>
                                            <span className="text-sm font-medium">{auth.policyStaff.email || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Status:</span>
                                            {isEditing.staffStatus ? (
                                                <select
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.policy_staff_status}
                                                    onChange={(e) => updatePolicyStaffField('policy_staff_status', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffStatus: false }))}
                                                    autoFocus
                                                >
                                                    <option value="live" className="bg-purple-700">Live</option>
                                                    <option value="draft" className="bg-purple-700">Draft</option>
                                                </select>
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1 capitalize"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffStatus: true }))}
                                                >
                                                    {auth.policyStaff.policy_staff_status}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Archived:</span>
                                            {isEditing.staffArchived ? (
                                                <select
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.archived}
                                                    onChange={(e) => updatePolicyStaffField('archived', e.target.value === 'true')}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffArchived: false }))}
                                                    autoFocus
                                                >
                                                    <option value="true" className="bg-purple-700">Yes</option>
                                                    <option value="false" className="bg-purple-700">No</option>
                                                </select>
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffArchived: true }))}
                                                >
                                                    {auth.policyStaff.archived ? 'Yes' : 'No'}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Inception Date:</span>
                                            {isEditing.staffInception ? (
                                                <input
                                                    type="date"
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.inception_date}
                                                    onChange={(e) => updatePolicyStaffField('inception_date', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffInception: false }))}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffInception: true }))}
                                                >
                                                    {auth.policyStaff.inception_date}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">End Date:</span>
                                            {isEditing.staffEndDate ? (
                                                <input
                                                    type="date"
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.end_date}
                                                    onChange={(e) => updatePolicyStaffField('end_date', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffEndDate: false }))}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffEndDate: true }))}
                                                >
                                                    {auth.policyStaff.end_date}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Date of Birth:</span>
                                            {isEditing.staffDob ? (
                                                <input
                                                    type="date"
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.date_of_birth}
                                                    onChange={(e) => updatePolicyStaffField('date_of_birth', e.target.value)}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffDob: false }))}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffDob: true }))}
                                                >
                                                    {auth.policyStaff.date_of_birth}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Accessed Slimming World:</span>
                                            {isEditing.staffSlimmingWorld ? (
                                                <select
                                                    className="text-sm font-medium bg-purple-600 text-white border border-purple-400 rounded px-1 py-0.5"
                                                    value={auth.policyStaff.accessed_slimming_world}
                                                    onChange={(e) => updatePolicyStaffField('accessed_slimming_world', e.target.value === 'true')}
                                                    onBlur={() => setIsEditing(prev => ({ ...prev, staffSlimmingWorld: false }))}
                                                    autoFocus
                                                >
                                                    <option value="true" className="bg-purple-700">Yes</option>
                                                    <option value="false" className="bg-purple-700">No</option>
                                                </select>
                                            ) : (
                                                <span
                                                    className="text-sm font-medium cursor-pointer hover:text-purple-100 flex items-center gap-1"
                                                    onClick={() => setIsEditing(prev => ({ ...prev, staffSlimmingWorld: true }))}
                                                >
                                                    {auth.policyStaff.accessed_slimming_world ? 'Yes' : 'No'}
                                                    <FontAwesomeIcon icon={faEdit} className="w-3 h-3 opacity-60" />
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Landline:</span>
                                            <span className="text-sm font-medium">{auth.policyStaff.phone_data?.landline || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-1 border-b border-purple-400/10">
                                            <span className="text-xs text-purple-200">Mobile:</span>
                                            <span className="text-sm font-medium">{auth.policyStaff.phone_data?.mobile || 'N/A'}</span>
                                        </div>


                                        <div className="border-t border-purple-400/20 pt-2">
                                            <button
                                                className="flex items-center justify-between w-full py-1 text-xs text-purple-200 hover:text-white cursor-pointer"
                                                onClick={() => setShowAddressSection(!showAddressSection)}
                                            >
                                                <span>Address Details</span>
                                                <FontAwesomeIcon
                                                    icon={showAddressSection ? faChevronUp : faChevronDown}
                                                    className="w-3 h-3"
                                                />
                                            </button>
                                            {showAddressSection && (
                                                <div className="mt-2 space-y-2 pl-2 border-l border-purple-400/20">
                                                    <div className="flex justify-between items-center py-1">
                                                        <span className="text-xs text-purple-200">Address 1:</span>
                                                        <span className="text-sm font-medium">{auth.policyStaff.address_data?.address_1 || 'N/A'}</span>
                                                    </div>
                                                    {auth.policyStaff.address_data?.address_2 && (
                                                        <div className="flex justify-between items-center py-1">
                                                            <span className="text-xs text-purple-200">Address 2:</span>
                                                            <span className="text-sm font-medium">{auth.policyStaff.address_data.address_2}</span>
                                                        </div>
                                                    )}
                                                    {auth.policyStaff.address_data?.address_3 && (
                                                        <div className="flex justify-between items-center py-1">
                                                            <span className="text-xs text-purple-200">Address 3:</span>
                                                            <span className="text-sm font-medium">{auth.policyStaff.address_data.address_3}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between items-center py-1">
                                                        <span className="text-xs text-purple-200">Town:</span>
                                                        <span className="text-sm font-medium">{auth.policyStaff.address_data?.town || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1">
                                                        <span className="text-xs text-purple-200">County:</span>
                                                        <span className="text-sm font-medium">{auth.policyStaff.address_data?.county || 'N/A'}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-1">
                                                        <span className="text-xs text-purple-200">Postcode:</span>
                                                        <span className="text-sm font-medium">{auth.policyStaff.address_data?.postcode || 'N/A'}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'staff' && !auth.policyStaff && (
                                <div className="text-center py-4">
                                    <span className="text-sm text-purple-200">No policy staff data available</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isHidden && (
                <button
                    onClick={handleShowBanner}
                    className="fixed top-4 right-4 z-[999] bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-110 border border-purple-300/20"
                    title="Show Policy Banner"
                >
                    <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                </button>
            )}
        </>
    )
}

export default PolicyBanner